import {
  ButtonHTMLAttributes,
  HTMLAttributes,
  LabelHTMLAttributes,
  ReactElement,
  ReactNode,
  Ref,
  RefAttributes,
  WeakValidationMap,
} from 'react';
import PropTypes from 'prop-types';
import forwardRefToProps from '../utils/forwardRefToProps';
import { forgeClassHelper } from '../utils/classes';

const classes = forgeClassHelper('label-button');

/** Specify the component's root element  */
export type LabelButtonAs = 'label' | 'button';
/** The list of LabelButton rendered root element supported */
export const labelButtonAs: readonly LabelButtonAs[] = ['label', 'button'];

/** Determine where to apply a border radius */
export type LabelButtonBorderRadius = 'all' | 'top' | 'bottom' | 'left' | 'right' | 'none';
/** The list of border radius placement supported */
export const labelButtonBorderRadius: readonly LabelButtonBorderRadius[] = [
  'all',
  'top',
  'bottom',
  'left',
  'right',
  'none',
];

/** Determine LabelButton's Button size */
export type LabelButtonSize = 'compact' | 'small' | 'medium' | 'large';
/** The list of LabelButton size supported */
export const labelButtonSize: readonly LabelButtonSize[] = ['compact', 'small', 'medium', 'large'];

/** The props that LabelButton destructures for its internal consumption.
 *
 * This is in contrast to LabelButtonForwardedProps, which are props forwarded to the
 * underlying DOM element.
 */
export interface LabelButtonDestructuredProps<As extends LabelButtonAs> {
  /** Set to true to style the button as active / selected */
  active?: boolean;

  /** Specify the component's root element  */
  as?: As;

  /** Where to apply a border radius */
  borderRadius?: LabelButtonBorderRadius;

  /** Content to render inside the button */
  children: ReactNode;

  /** CSS class to apply to the button */
  className?: string;

  /** Set to true to style the LabelButton as focused. (Necessary since label's can't actually be focused) */
  focused?: boolean;

  /** Set to true to style LabelButton to display full width  */
  fullWidth?: boolean;

  /** Button size */
  size?: LabelButtonSize;
}

/** The prop keys consumed directly by the LabelButton component */
export type KeyofLabelButtonProps = keyof LabelButtonDestructuredProps<LabelButtonAs>;

/** The props forwarded by LabelButton to its underlying native DOM element.
 *
 * This type is only applied at the last possible second because the keys of
 * this type change depending on UseLink. This confuses Typescript.
 * Instead, LabelButtonDestructuredProps hold the props that we care about
 * and type assertions to LabelButtonForwardedProps are done where necessary.
 */
export type LabelButtonForwardedProps<As extends LabelButtonAs> = As extends 'button'
  ? Omit<ButtonHTMLAttributes<HTMLButtonElement>, KeyofLabelButtonProps>
  : Omit<LabelHTMLAttributes<HTMLLabelElement>, KeyofLabelButtonProps>;

/** The LabelButton Props for the final LabelButton component
 *
 * Contains advanced type checking. Ensures that incompatible props aren't used together.
 */
export type LabelButtonProps<As extends LabelButtonAs> = LabelButtonDestructuredProps<As> &
  LabelButtonForwardedProps<As>;

/** The props used to implement LabelButton. */
export type LabelButtonComponentProps<As extends LabelButtonAs> = LabelButtonProps<As> & {
  forwardedRef?: Ref<LabelButtonHTMLElement<As>>;
};

/** The HTML Element rendered by LabelButton.
 *
 * Dependent on As */
export type LabelButtonHTMLElement<As extends LabelButtonAs> = As extends 'button'
  ? HTMLButtonElement
  : HTMLLabelElement;

/**
 * LabelButton
 *
 * Used in the implementations of RadioButton and CheckboxButton.
 * Also intended to be used in the `buttons` prop of ComboButton.
 * @documentedBy ComboButton
 */
export const LabelButtonComponent = <As extends LabelButtonAs>({
  children,
  as = 'button' as As,
  className,
  size = 'medium',
  active = false,
  focused = false,
  fullWidth = false,
  borderRadius = 'all',
  forwardedRef,
  ...rest
}: LabelButtonComponentProps<As>): ReactElement => {
  const propsToApply = {
    ...rest,
    ...classes({
      modifiers: {
        [size]: true,
        active: active,
        focused: focused,
        'full-width': fullWidth,
        [`border-radius-${borderRadius}`]: true,
      },
      extra: className,
    }),
  };

  return as === 'button' ? (
    <button
      type="button"
      {...(propsToApply as HTMLAttributes<HTMLButtonElement>)}
      ref={forwardedRef as Ref<HTMLButtonElement>}
    >
      {children}
    </button>
  ) : (
    <label {...(propsToApply as HTMLAttributes<HTMLLabelElement>)} ref={forwardedRef as Ref<HTMLLabelElement>}>
      {children}
    </label>
  );
};

export const labelButtonPropTypes: WeakValidationMap<LabelButtonProps<LabelButtonAs>> = {
  /** Set to true to style the button as active / selected */
  active: PropTypes.bool,

  /** Specify the component's root element  */
  as: PropTypes.oneOf(labelButtonAs),

  /** Specify which side(s) of the button should have a border radius */
  borderRadius: PropTypes.oneOf(labelButtonBorderRadius),

  /** Content to render inside the button */
  children: PropTypes.any.isRequired,

  /** CSS class to apply to the button */
  className: PropTypes.string,

  /** Set to true to style the LabelButton as focused. (Necessary since label's can't actually be focused) */
  focused: PropTypes.bool,

  /** Set to true to style LabelButton to display full width  */
  fullWidth: PropTypes.bool,

  /** Button size */
  size: PropTypes.oneOf(labelButtonSize),
};

LabelButtonComponent.displayName = 'LabelButton';
LabelButtonComponent.propTypes = labelButtonPropTypes;

export type LabelButtonDefaultExport = {
  <As extends LabelButtonAs = 'button'>(
    props: LabelButtonProps<As> & RefAttributes<LabelButtonHTMLElement<As>>
  ): ReturnType<typeof LabelButtonComponent>;
  supportedProps?: Array<keyof LabelButtonProps<LabelButtonAs>>;
};

const LabelButton = forwardRefToProps(LabelButtonComponent) as LabelButtonDefaultExport;
LabelButton.supportedProps = ['borderRadius', 'active', 'size'];
export default LabelButton;
